import { useNavigate } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import { fileURL } from "../../../utils/fileURL";
import "./Cart.css";
import { useEffect, useRef, useState } from "react";
import { validateCart } from "../../../services/public/cartValidation";
import { validateClienteSession } from "../../../services/authCliente";
import CheckoutAuthModal from "./CheckoutAuthModal";
import { IconCartEmpty } from "../../../icons/Icons";

const formatMoney = (value) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(Number(value));

/**
 * Que decirle al cliente segun lo que devolvio el backend.
 *
 * "Inactivo" y "no publicado" se cuentan como no disponible: son decisiones
 * internas y al comprador no le dicen nada. Lo que si se dice, y es lo que
 * pidio saber, es cuantas unidades hay cuando quedan menos de las que
 * pidio.
 */
const textoDeMotivo = (b) => {
  switch (b?.motivo) {
    case "stock_parcial":
      return `only ${b.available} ${
        b.available === 1 ? "unit" : "units"
      } left, you asked for ${b.requested}`;
    case "sin_stock":
      return "out of stock";
    case "inactivo":
    case "no_publicado":
    case "no_existe":
      return "no longer available";
    default:
      return "not available right now";
  }
};

function Cart() {
  const navigate = useNavigate();
  const {
    cart,
    replaceCart,
    updateQty,
    remove,
    subtotal,
    coreChargeTotal,
    grandTotal,
    ready,
  } = useCart();

  const [notice, setNotice] = useState(null);

  // Las lineas que no se pueden comprar, con el motivo de cada una. Se
  // guardan para poder nombrar el producto en vez de decir "algo salio mal".
  const [bloqueos, setBloqueos] = useState([]);

  /* Los precios que cambiaron desde que el cliente agrego el producto. El
     carrito vive en el navegador y puede tener dias; el cobro sale del
     precio de la base, asi que sin avisar el cliente veia un total y le
     llegaba una factura con otro. */
  const [cambiosPrecio, setCambiosPrecio] = useState([]);

  // Para que la revision de entrada corra una sola vez y no en cada cambio.
  const revisadoRef = useRef(false);
  const [validating, setValidating] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const isLoading = validating || blocking;

  const [showCheckoutAuth, setShowCheckoutAuth] = useState(false);

  /* En centavos, para no comparar decimales: 465.1 !== 465.10 en coma
     flotante y eso marcaria como cambiado un precio que es el mismo. */
  const enCentavos = (v) => Math.round(Number(v || 0) * 100);

  /* Devuelve que lineas cambiaron de precio y el carrito ya actualizado.
     Lo usan las dos validaciones, la de entrada y la del boton. */
  const compararPrecios = (carrito, lineas) => {
    const cambios = [];

    const actualizado = carrito.map((item) => {
      const linea = lineas.find((i) => i.producto_id === item.producto_id);
      if (!linea || linea.precio_unitario === null) return item;

      const antes = enCentavos(item.precio_unitario);
      const ahora = enCentavos(linea.precio_unitario);
      const coreAntes = enCentavos(item.core_charge);
      const coreAhora = enCentavos(linea.core_charge);

      if (antes !== ahora || coreAntes !== coreAhora) {
        cambios.push({
          nombre: linea.nombre || item.nombre,
          antes: Number(item.precio_unitario || 0),
          ahora: Number(linea.precio_unitario || 0),
          core_antes: Number(item.core_charge || 0),
          core_ahora: Number(linea.core_charge || 0),
        });
      }

      return {
        ...item,
        precio_unitario: Number(linea.precio_unitario),
        core_charge: Number(linea.core_charge || 0),
      };
    });

    return { cambios, actualizado };
  };

  useEffect(() => {
    const runValidation = async () => {
      try {
        setValidating(true);

        const result = await validateCart(cart);

        let adjusted = false;

        const conStock = cart.map((item) => {
          const validated = result.items.find(
            (i) => i.producto_id === item.producto_id
          );

          if (!validated) return item;

          if (validated.allowed !== item.cantidad) {
            adjusted = true;
          }

          return {
            ...item,
            nombre: item.nombre,
            cantidad: validated.allowed,
            stock: validated.available,
          };
        });

        const { cambios, actualizado } = compararPrecios(
          conStock,
          result.items
        );

        setCambiosPrecio(cambios);

        if (adjusted || cambios.length > 0) {
          replaceCart(actualizado);
        }

        setNotice(
          adjusted ? "Some items were adjusted due to stock changes." : null
        );
      } catch (err) {
        console.error("Cart validation failed", err);
        setNotice("We couldn't verify stock at this moment.");
      } finally {
        setValidating(false);
      }
    };

    /* Esta revision no corria nunca. Las dependencias eran `[]`, asi que
       se ejecutaba en el primer render, y en ese momento `cart` todavia
       es el arreglo vacio: `useCart` lo llena en su propio efecto, que va
       despues. El `cart.length > 0` daba falso y no se validaba nada, asi
       que el carrito mostraba lo que tuviera guardado el navegador sin
       contrastarlo con la base. Ahora espera a que el carrito este leido
       y el ref se encarga de que sea una sola vez. */
    if (!ready) return;
    if (revisadoRef.current) return;
    if (cart.length === 0) return;

    revisadoRef.current = true;
    runValidation();
  }, [ready, cart]);

  /**
   * Antes de dejar avanzar: se revisa el carrito contra el backend.
   *
   * Recien si las tres condiciones se cumplen para todas las lineas —activo,
   * publicado y con stock para lo pedido— se abre el modal de cuenta. Si algo
   * falla se dice cual producto y por que, y el carrito se ajusta a lo que si
   * hay.
   */
  const handleCheckout = async () => {
    try {
      setBlocking(true);
      setNotice(null);
      setBloqueos([]);

      const result = await validateCart(cart);
      const lineas = Array.isArray(result?.items) ? result.items : [];

      /* Primero el precio. Si cambio desde que lo agrego, se actualiza el
         carrito, se le dice cual y cuanto, y este click no avanza: el
         siguiente si, porque para entonces el total que ve en pantalla es
         el que se le va a cobrar. */
      const { cambios, actualizado } = compararPrecios(cart, lineas);

      if (cambios.length > 0) {
        replaceCart(actualizado);
        setCambiosPrecio(cambios);
        return;
      }

      setCambiosPrecio([]);

      // El backend ya dice si se puede avanzar; aca solo se confia en eso.
      if (result?.puedeAvanzar) {
        // Con sesion activa no se pide iniciarla de nuevo: se pasa derecho.
        // Se pregunta al backend en vez de mirar si hay token guardado,
        // porque un token vencido mandaria al checkout a fallar ahi.
        const sesion = await validateClienteSession();

        if (sesion?.ok) {
          navigate("/store/checkout");
        } else {
          setShowCheckoutAuth(true);
        }

        return;
      }

      const problemas = lineas.filter((i) => i.status !== "ok");
      setBloqueos(problemas);

      // El carrito se deja en lo que realmente se puede comprar: lo que no
      // hay se pone en cero y queda a la vista, no se borra a espaldas del
      // cliente.
      const nextCart = cart.map((item) => {
        const validado = lineas.find((i) => i.producto_id === item.producto_id);
        if (!validado) return item;

        return {
          ...item,
          cantidad: validado.allowed,
          stock: validado.available,
        };
      });

      replaceCart(nextCart);
    } catch (err) {
      console.error("Cart validation failed", err);
      setNotice(
        err?.message || "We couldn't verify your cart. Please try again."
      );
    } finally {
      setBlocking(false);
    }
  };

  const hasOutOfStock = cart.some((item) => item.stock === 0);

  if (cart.length === 0) {
    return (
      <div className="adc-cart-empty">
        <div className="adc-cart-empty__card">
          <div className="adc-cart-empty__icon">{IconCartEmpty}</div>

          <h2>Your cart is empty</h2>
          <p>
            Looks like you haven’t added anything yet. Explore the store and find what
            you need.
          </p>

          <button
            className="adc-cart-back adc-cart-empty__btn"
            onClick={() => navigate("/store")}
          >
            ← Continue shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="adc-cart-page">
      {isLoading && (
        <div className="adc-cart-overlay">
          <div className="adc-cart-spinner"></div>
          <p>
            {blocking ? "Confirming stock availability…" : "Checking stock availability…"}
          </p>
        </div>
      )}

      <button className="adc-cart-back" onClick={() => navigate("/store")}>
        ← Continue shopping
      </button>

      <h1>Shopping Cart</h1>

      {notice && <div className="adc-cart-notice">{notice}</div>}

      {cambiosPrecio.length > 0 && (
        <div className="adc-cart-precios">
          <strong>
            {cambiosPrecio.length === 1
              ? "One price changed since you added it:"
              : `${cambiosPrecio.length} prices changed since you added them:`}
          </strong>

          <ul>
            {cambiosPrecio.map((c, i) => (
              <li key={i}>
                {c.nombre}: {formatMoney(c.antes)} &rarr;{" "}
                <strong>{formatMoney(c.ahora)}</strong>
                {c.core_antes !== c.core_ahora ? (
                  <>
                    {" "}
                    &middot; core charge {formatMoney(c.core_antes)} &rarr;{" "}
                    <strong>{formatMoney(c.core_ahora)}</strong>
                  </>
                ) : null}
              </li>
            ))}
          </ul>

          <span className="adc-cart-precios-pie">
            The total above is already updated. Continue when you agree with
            it.
          </span>
        </div>
      )}

      {bloqueos.length > 0 && (
        <div className="adc-cart-bloqueo">
          <p className="adc-cart-bloqueo-titulo">
            {bloqueos.length === 1
              ? "One item can't be ordered right now:"
              : `${bloqueos.length} items can't be ordered right now:`}
          </p>

          <ul className="adc-cart-bloqueo-lista">
            {bloqueos.map((b, i) => (
              <li key={`${b.producto_id}-${i}`}>
                <strong>{b.nombre || `Product #${b.producto_id}`}</strong>
                {b.no_parte ? ` (P/N ${b.no_parte})` : ""} — {textoDeMotivo(b)}
              </li>
            ))}
          </ul>

          <p className="adc-cart-bloqueo-pie">
            Your cart was updated with what we can ship. Remove or adjust these
            items to continue.
          </p>
        </div>
      )}

      {(validating || blocking) && (
        <div className="adc-cart-notice">Validating stock availability…</div>
      )}

      {/* Dos columnas: lo que se lleva a la izquierda y lo que se paga a la
          derecha. Antes el resumen iba debajo de la lista, asi que con
          cuatro productos el total y el boton de pagar quedaban fuera de
          pantalla. */}
      <div className="adc-cart-split">
        <div className="adc-cart-list">
        {cart.map((item) => {
          const price = Number(item.precio_unitario);
          const core = Number(item.core_charge || 0);
          const stock = Number(item.stock ?? 0);

          const totalItem = price * item.cantidad;
          const coreItemTotal = core * item.cantidad; 

          const isMax = item.cantidad >= stock;
          const outOfStock = stock === 0;

          return (
            <div key={item.producto_id} className="adc-cart-item">
              <img
                src={fileURL(item.imagen)}
                alt={item.nombre}
                className="adc-cart-img"
                onClick={() => navigate(`/store/product/${item.producto_id}`)}
              />

              <div className="adc-cart-info">
                <h3>{item.nombre}</h3>

                <p className="adc-cart-price">{formatMoney(price)}</p>

                {core > 0 && (
                  <p
                    className="adc-cart-core"
                  >
                  Core charge: {formatMoney(core)}
                  </p>
                )}

                <span
                  className={`adc-cart-stock ${outOfStock ? "out" : isMax ? "limit" : "in"}`}
                >
                  {outOfStock ? "Out of stock" : `Available: ${stock}`}
                </span>

                <button
                  className="adc-cart-view"
                  onClick={() => navigate(`/store/product/${item.producto_id}`)}
                >
                  View product
                </button>
              </div>

              <div className="adc-cart-qty-control">
                <button
                  onClick={() =>
                    updateQty(item.producto_id, Math.max(1, item.cantidad - 1))
                  }
                  disabled={item.cantidad <= 1 || isLoading}
                >
                  −
                </button>

                <input
                  type="number"
                  min="1"
                  max={stock}
                  value={item.cantidad}
                  disabled={outOfStock || isLoading}
                  onChange={(e) => {
                    let next = Number(e.target.value);

                    if (isNaN(next) || next < 1) next = 1;
                    if (next > stock) next = stock;

                    updateQty(item.producto_id, next);
                  }}
                />

                <button
                  onClick={() =>
                    updateQty(item.producto_id, Math.min(stock, item.cantidad + 1))
                  }
                  disabled={isMax || outOfStock || isLoading}
                >
                  +
                </button>
              </div>

              <p className="adc-cart-total">{formatMoney(totalItem)}</p>

              <button className="adc-cart-remove" onClick={() => remove(item.producto_id)}>
                ✕
              </button>
            </div>
          );
          })}
        </div>

        <aside className="adc-cart-summary">
          <h2 className="adc-cart-summary-titulo">Order summary</h2>

          <div className="adc-cart-subtotal">
            <span>Subtotal</span>
            <strong>{formatMoney(subtotal)}</strong>
          </div>

          {coreChargeTotal > 0 && (
            <div className="adc-cart-subtotal adc-cart-subtotal--core">
              <span>Core charge total</span>
              <strong>{formatMoney(coreChargeTotal)}</strong>
            </div>
          )}

          {coreChargeTotal > 0 && (
            <div className="adc-cart-subtotal adc-cart-subtotal--total">
              <span>Total</span>
              <strong>{formatMoney(grandTotal)}</strong>
            </div>
          )}

          {/* Decia que el envio se calculaba en el checkout y no se calcula
              en ningun lado: lo cobra el courier contra la cuenta del
              cliente. */}
          <p className="adc-cart-nota">
            Shipping is billed by your carrier.
          </p>

          {/* Valida contra el backend y recien entonces abre el modal de
              cuenta: antes lo abria de una, sin revisar nada. */}
          <button
            className="adc-btn adc-next-btn"
            disabled={hasOutOfStock || validating || blocking}
            onClick={handleCheckout}
          >
            {blocking ? "Checking availability…" : "Proceed to checkout"}
          </button>

        </aside>
      </div>

      {/* Fuera del resumen: ese panel es sticky, y sticky crea un contexto
          de apilamiento propio. Adentro, el overlay con z-index 9999 solo
          competia contra los hermanos del panel, asi que el pie de pagina
          se dibujaba encima y cortaba el modal por la mitad. */}
      <CheckoutAuthModal
        open={showCheckoutAuth}
        onClose={() => setShowCheckoutAuth(false)}
      />
    </div>
  );
}

export default Cart;
